import React, { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Scissors, Droplets, User, Users } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

// Tag each chair "revenue" (expects a sale) or "ancillary" (wash/processing — never expects a sale).
export default function StationRoles() {
  const qc = useQueryClient();
  const [storeId, setStoreId] = useState(null);
  const { data: stores } = useQuery({ queryKey: ["stores"], queryFn: () => api.get("/stores").then((r) => r.data) });
  useEffect(() => { if (stores?.length && !storeId) setStoreId(stores[0].id); }, [stores, storeId]);
  const { data: store } = useQuery({
    queryKey: ["store", storeId],
    queryFn: () => api.get(`/stores/${storeId}`).then((r) => r.data),
    enabled: !!storeId,
  });

  const mode = store?.mode === "salon" ? "salon" : "barbershop";
  const setMode = async (m) => {
    try {
      await api.put(`/stores/${storeId}`, { mode: m });
      await qc.invalidateQueries({ queryKey: ["store", storeId] });
      qc.invalidateQueries({ queryKey: ["stores"] });
      toast.success(m === "salon" ? "Salon mode — multi-station session stitching on" : "Barbershop mode — single-chair fast reconciliation");
    } catch { toast.error("Corporate/owner access required for this store"); }
  };

  const setRole = async (chair, role) => {
    try {
      await api.patch(`/stores/${storeId}/chairs/${chair.id}`, { role });
      await qc.invalidateQueries({ queryKey: ["store", storeId] });
      qc.invalidateQueries({ queryKey: ["stores"] });
      toast.success(`${chair.label} → ${role === "ancillary" ? "Ancillary" : "Revenue"}`);
    } catch { toast.error("Corporate/owner access required for this store"); }
  };

  const chairs = store?.chairs || [];

  return (
    <div className="surface-card p-6 space-y-5" data-testid="station-roles">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-display text-lg font-medium">Station roles</h2>
          <p className="text-xs text-slate-400 mt-1 max-w-xl">
            Tag each chair. <strong className="text-slate-300">Revenue</strong> stations (cut, colour, styling) expect a sale;
            <strong className="text-slate-300"> ancillary</strong> stations (wash basins, processing seats) never do — so a client
            being washed or left to develop never raises a phantom charge.
          </p>
        </div>
        <Select value={storeId || ""} onValueChange={setStoreId}>
          <SelectTrigger className="w-60 bg-[#1E2638] border-[#2A3550]" data-testid="station-store-select">
            <SelectValue placeholder="Select store" />
          </SelectTrigger>
          <SelectContent className="bg-[#1E2638] border-[#2A3550] max-h-72">
            {(stores || []).map((s) => (
              <SelectItem key={s.id} value={s.id} data-testid={`station-store-${s.number}`}>#{s.number} · {s.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Store mode profile — barbershop (stitching off) vs salon (multi-station stitching) */}
      <div className="rounded-lg border border-[#2A3550] bg-[#171E2E] p-4" data-testid="store-mode-profile">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="text-sm font-medium text-white">Store mode</div>
            <p className="text-xs text-slate-400 mt-0.5 max-w-md">
              {mode === "salon"
                ? "Salon — one client is stitched across wash, cut & colour chairs into a single ticket."
                : "Barbershop — one chair, one client, one sale: fast single-chair reconciliation with stitching off."}
            </p>
          </div>
          <div className="inline-flex rounded-lg border border-[#2A3550] bg-[#1E2638] p-0.5 shrink-0">
            <button data-testid="mode-barbershop" onClick={() => setMode("barbershop")}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-md transition-colors duration-200 ${mode === "barbershop" ? "bg-[#38BDF8]/15 text-[#38BDF8]" : "text-slate-400 hover:text-white"}`}>
              <User className="h-3.5 w-3.5" /> Barbershop
            </button>
            <button data-testid="mode-salon" onClick={() => setMode("salon")}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-md transition-colors duration-200 ${mode === "salon" ? "bg-[#38BDF8]/15 text-[#38BDF8]" : "text-slate-400 hover:text-white"}`}>
              <Users className="h-3.5 w-3.5" /> Salon
            </button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {chairs.map((c) => {
          const role = c.role === "ancillary" ? "ancillary" : "revenue";
          return (
            <div key={c.id} data-testid={`station-row-${c.id}`}
              className="flex items-center justify-between rounded-lg border border-[#2A3550] bg-[#171E2E] px-4 py-3">
              <div className="text-sm font-medium text-white">{c.label}</div>
              <div className="inline-flex rounded-lg border border-[#2A3550] bg-[#1E2638] p-0.5">
                <button
                  data-testid={`station-${c.id}-revenue`}
                  onClick={() => setRole(c, "revenue")}
                  className={`inline-flex items-center gap-1 px-2.5 py-1 text-xs rounded-md transition-colors duration-200 ${role === "revenue" ? "bg-[#38BDF8]/15 text-[#38BDF8]" : "text-slate-400 hover:text-white"}`}
                >
                  <Scissors className="h-3 w-3" /> Revenue
                </button>
                <button
                  data-testid={`station-${c.id}-ancillary`}
                  onClick={() => setRole(c, "ancillary")}
                  className={`inline-flex items-center gap-1 px-2.5 py-1 text-xs rounded-md transition-colors duration-200 ${role === "ancillary" ? "bg-slate-500/25 text-slate-200" : "text-slate-400 hover:text-white"}`}
                >
                  <Droplets className="h-3 w-3" /> Ancillary
                </button>
              </div>
            </div>
          );
        })}
        {store && chairs.length === 0 && (
          <div className="text-sm text-slate-400 py-6 text-center col-span-full">This store has no chairs yet.</div>
        )}
      </div>
      <p className="text-[11px] text-slate-500">
        Works with journey stitching: if a client moves from a revenue chair to an ancillary station and back, the visit is still counted as
        <strong className="text-slate-400"> one session, one ticket</strong>.
      </p>
    </div>
  );
}
